import type { Context } from '@fortune-sheet/core';
import {
    type WorkbookInternals,
    hasPointModeSupport,
    rowLocationByIndex,
    colLocationByIndex,
    rangeSetValue,
    createRangeHightlight,
    createFormulaRangeSelect,
    rangeHightlightselected,
    handleFormulaInput,
    functionHTMLGenerate,
} from './workbookInternals';
import { CELL_INPUT_ID, FORMULA_BAR_ID } from './selectors';
import { isRefInsertablePosition, nextPointCell, type ArrowKey, type PointCell } from './formulaPointMode';
import { cycleRefAtCaret } from './formulaRefs';
import { liveDims } from './navigation';

// The library's own formula-function autocomplete dropdown (FormulaSearch) —
// only mounted in the DOM while it has candidates, so existence = visible.
// While it's up, arrow keys navigate the dropdown, not the grid — and Tab
// accepts its suggestion instead of committing the cell (keyDispatch.ts's
// `tabCommit`).
export const FORMULA_SEARCH_ID = 'luckysheet-formula-search-c';

/** Flattened caret offset across every descendant text node of `el` — needed
 *  because the formula editor renders each token as its own <span>, so a
 *  plain `Range.startOffset` alone is relative to whichever span/text node
 *  currently holds the caret, not the element's full text. */
const getCaretOffset = (el: HTMLElement): number => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return el.textContent?.length ?? 0;
    const range = selection.getRangeAt(0);
    if (!el.contains(range.startContainer)) return el.textContent?.length ?? 0;
    const preRange = range.cloneRange();
    preRange.selectNodeContents(el);
    preRange.setEnd(range.startContainer, range.startOffset);
    return preRange.toString().length;
};

const setCaretOffset = (el: HTMLElement, offset: number): void => {
    const selection = window.getSelection();
    if (!selection) return;
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let remaining = offset;
    let node = walker.nextNode();
    while (node) {
        const len = node.textContent?.length ?? 0;
        if (remaining <= len) {
            const range = document.createRange();
            range.setStart(node, remaining);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
            return;
        }
        remaining -= len;
        node = walker.nextNode();
    }
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
};

const editedCell = (ctx: Context): PointCell => ({ r: ctx.luckysheetCellUpdate[0] ?? 0, c: ctx.luckysheetCellUpdate[1] ?? 0 });

/** Derives the in-progress point-mode {anchor, cursor} from the context's own
 *  `func_selectedrange` (what the PREVIOUS step, or the mouse branch, wrote):
 *  the cursor is whichever end matches `row_focus`/`column_focus`; the anchor
 *  is the other one. */
const pointFromSelection = (sel: NonNullable<Context['formulaCache']['func_selectedrange']>): { anchor: PointCell; cursor: PointCell } => {
    const cursor: PointCell = { r: sel.row_focus ?? sel.row[0], c: sel.column_focus ?? sel.column[0] };
    const anchor: PointCell = {
        r: sel.row[0] === cursor.r ? sel.row[1] : sel.row[0],
        c: sel.column[0] === cursor.c ? sel.column[1] : sel.column[0],
    };
    return { anchor, cursor };
};

/**
 * One arrow-key step of formula point-mode, mirroring the mouse branch's own
 * recipe (`core:77518-77568`) exactly — same
 * `func_selectedrange`/highlight/rangeSetValue sequence, just driven by
 * `nextPointCell` instead of a mouse position.
 */
const runPointModeStep = (internals: WorkbookInternals, key: ArrowKey, shift: boolean): void => {
    internals.setContextWithProduce((d) => {
        const refs = internals.getRefs();
        const sheet = d.luckysheetfile.find((s) => s.id === d.currentSheetId);
        const bounds = liveDims(sheet);

        const current =
            d.formulaCache.rangestart && d.formulaCache.func_selectedrange
                ? pointFromSelection(d.formulaCache.func_selectedrange)
                : { anchor: editedCell(d), cursor: editedCell(d) };
        const next = nextPointCell(current.anchor, current.cursor, key, shift, bounds);

        const row: [number, number] = [Math.min(next.anchor.r, next.cursor.r), Math.max(next.anchor.r, next.cursor.r)];
        const column: [number, number] = [Math.min(next.anchor.c, next.cursor.c), Math.max(next.anchor.c, next.cursor.c)];

        const [rTop] = rowLocationByIndex!(row[0], d.visibledatarow);
        const [, rBot] = rowLocationByIndex!(row[1], d.visibledatarow);
        const [cLeft] = colLocationByIndex!(column[0], d.visibledatacolumn);
        const [, cRight] = colLocationByIndex!(column[1], d.visibledatacolumn);
        const left = cLeft;
        const top = rTop;
        const width = cRight - cLeft - 1;
        const height = rBot - rTop - 1;

        d.formulaCache.func_selectedrange = {
            left,
            width,
            top,
            height,
            left_move: left,
            width_move: width,
            top_move: top,
            height_move: height,
            row,
            column,
            row_focus: next.cursor.r,
            column_focus: next.cursor.c,
        };
        rangeSetValue!(d, refs.cellInput, { row, column }, refs.fxInput);
        d.formulaCache.rangestart = true;
        d.formulaCache.rangedrag_column_start = false;
        d.formulaCache.rangedrag_row_start = false;
        d.formulaCache.selectingRangeIndex = d.formulaCache.rangechangeindex ?? 0;
        if ((d.formulaCache.rangechangeindex ?? 0) > (d.formulaRangeHighlight?.length ?? 0)) {
            createRangeHightlight!(d, refs.cellInput?.innerHTML ?? '', d.formulaCache.rangechangeindex);
        }
        createFormulaRangeSelect!(d, { rangeIndex: d.formulaCache.rangechangeindex || 0, left, top, width, height });
        try {
            rangeHightlightselected!(d, refs.cellInput);
        } catch (err) {
            // Only refreshes the function hint; a throw inside the state update unmounts the grid.
            console.warn('[SheetEditor] point-mode hint failed', err);
        }
        // A key press is a finished pick, like the mouse branch's drag end. Left set, the
        // library keeps the dashed box on the grid after the formula is committed.
        d.formulaCache.selectingRangeIndex = -1;
    });
};

/**
 * The library's range helpers index the editor's child SPANs; on a bare text node they throw
 * inside the state update, which unmounts the whole grid. The library leaves text bare when it
 * starts with "+"/"-" (typing "-" then an arrow key crashed the editor) or when the first "="
 * was typed before the editor had focus. A "+"/"-" start becomes "=+…"/"=-…", which is also
 * what makes the committed text a formula. False = leave the key to the browser.
 */
const ensureFormulaSpans = (el: HTMLElement, text: string, caret: number): boolean => {
    const trimmed = text.trimStart();
    const formula = trimmed.startsWith('=') ? trimmed : `=${trimmed}`;
    if (formula === text && Array.from(el.childNodes).every((node) => node.nodeName === 'SPAN')) return true;
    if (formula.includes('<')) return false; // functionHTMLGenerate does not escape markup
    el.innerHTML = functionHTMLGenerate!(formula);
    setCaretOffset(el, Math.max(0, caret + formula.length - text.length));
    return true;
};

/** True when `text` is (or is starting to become) a formula — Excel also
 *  accepts a bare leading '+' or '-' as formula shorthand (auto-prefixed
 *  with '=' on commit), not just '='. Shared by handlePointMode/
 *  handleCycleRef's own "only intercept while editing a formula" guard. */
const looksLikeFormula = (text: string): boolean => /^[=+-]/.test(text.trimStart());

/**
 * Point-mode entry point. Returns `false` (not intercepted — the caret should
 * move natively) when: the point-mode support functions are missing (no
 * patch), the formula autocomplete dropdown is open, or the caret isn't at
 * an insertable position (matches core's own `israngeseleciton` gate).
 */
export const handlePointMode = (internals: WorkbookInternals, target: EventTarget | null, key: ArrowKey, shift: boolean): boolean => {
    if (!hasPointModeSupport) return false;
    if (document.getElementById(FORMULA_SEARCH_ID)) return false;
    if (!(target instanceof HTMLElement)) return false;
    const text = target.textContent ?? '';
    // Point mode only makes sense while editing a formula — mirrors
    // handleCycleRef's own guard.
    // Without it, an arrow key in plain text (e.g. "Invoice A1") at an
    // otherwise ref-insertable caret position inserted a range reference
    // into it instead of just moving the caret natively.
    if (!looksLikeFormula(text)) return false;
    const caret = getCaretOffset(target);
    // Once point mode has started, the caret sits right after the ref THIS
    // step just inserted (core's own rangeSetValue leaves it there too) — the
    // insertable-position gate only decides whether a FRESH point mode may
    // START, not whether an already-running one may continue.
    const fc = internals.getContext().formulaCache;
    const continuing = Boolean(fc?.rangestart && fc?.func_selectedrange);
    if (!continuing && !isRefInsertablePosition(text, caret)) return false;
    if (!continuing && !ensureFormulaSpans(target, text, caret)) return false;

    runPointModeStep(internals, key, shift);
    return true;
};

const otherEditor = (
    refs: { cellInput: HTMLDivElement | null; fxInput: HTMLDivElement | null },
    target: HTMLElement,
): HTMLDivElement | null => (target.id === CELL_INPUT_ID ? refs.fxInput : refs.cellInput);

/**
 * F4: cycles the $/relative state of the ref under the caret. `target`
 * is whichever editor (cell input or formula bar) is focused — F4 is
 * not gated on `editing` (root cause: the library's own F4 handler is a
 * permanent no-op, so there's no "not editing" behaviour to protect), but a
 * caret with no ref under it is simply a no-op here too.
 */
export const handleCycleRef = (internals: WorkbookInternals, target: EventTarget | null): boolean => {
    if (!hasPointModeSupport) return false;
    if (!(target instanceof HTMLElement) || (target.id !== CELL_INPUT_ID && target.id !== FORMULA_BAR_ID)) return false;
    const text = target.textContent ?? '';
    // Excel only toggles references while editing a formula — plain text
    // (e.g. "Invoice A1") has no ref to cycle, even though it happens to
    // contain a ref-shaped token.
    if (!looksLikeFormula(text)) return false;
    const caret = getCaretOffset(target);
    const cycled = cycleRefAtCaret(text, caret);
    if (cycled.text === text) return false;

    // Re-run through the library's own span generator instead of a flat
    // `textContent` write — a flattened editor gives
    // `handleFormulaInput` a caret whose parent is the editor DIV, not a
    // SPAN, which crashes core's `setCaretPosition`. The DOM already
    // holds the cycled text before this call, so a throw here is safe to swallow.
    //
    // Security: functionHTMLGenerate is a DOM XSS sink. For non-formula text
    // (no leading '=') it returns the string completely unescaped; even for a
    // formula, its per-substring concatenation never escapes a literal
    // '<'/'>'/'&' inside a text run. Only take the innerHTML/span path when
    // the cycled text is an honest formula with no '<' anywhere in it —
    // everything else (plain cell text, clipboard-pasted markup, a formula
    // with a '<' comparison) falls back to the same plain `textContent` write
    // the prior code used, which cannot execute injected markup.
    if (cycled.text.startsWith('=') && !cycled.text.includes('<')) {
        target.innerHTML = functionHTMLGenerate!(cycled.text);
    } else {
        target.textContent = cycled.text;
    }
    setCaretOffset(target, cycled.caret);
    internals.setContextWithProduce((d) => {
        const refs = internals.getRefs();
        try {
            handleFormulaInput!(d, otherEditor(refs, target), target, undefined);
        } catch (err) {
            console.warn('[SheetEditor] F4 reformat failed', err);
        }
    });
    return true;
};
